import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert } from '../ui';
import { AuthPage, Label } from './auth-parts';

/**
 * ON-06 — the one screen a retailer with a system-generated password sees
 * before anything else. `RetailerGuard` refuses every other retailer route
 * while `mustChangePassword` is true; this is the client-side experience of
 * that gate, not the gate itself.
 */
export function ChangePassword() {
  const { t } = useTranslation();
  const { refreshMe, signOut } = useSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 12) {
      setError(t('auth.changePassword.tooShort'));
      return;
    }
    if (newPassword !== confirm) {
      setError(t('auth.changePassword.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      await refreshMe();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.changePassword.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage
      eyebrow={t('auth.eyebrow')}
      title={t('auth.changePassword.title')}
      lede={t('auth.changePassword.body')}
    >
      <form onSubmit={(e) => void submit(e)} noValidate>
        {error ? <Alert tone="stop">{error}</Alert> : null}

        <div className="mk-auth-field">
          <Label htmlFor="currentPassword" required>{t('auth.changePassword.current')}</Label>
          <input
            id="currentPassword" type="password" required autoComplete="current-password"
            value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>

        <div className="mk-auth-field">
          <Label htmlFor="newPassword" required>{t('auth.changePassword.new')}</Label>
          <input
            id="newPassword" type="password" required autoComplete="new-password" minLength={12}
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div className="mk-auth-field">
          <Label htmlFor="confirmPassword" required>{t('auth.changePassword.confirm')}</Label>
          <input
            id="confirmPassword" type="password" required autoComplete="new-password" minLength={12}
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <button className="mk-auth-submit" type="submit" disabled={busy}>
          {busy ? t('auth.submitting') : t('auth.changePassword.submit')}
        </button>
      </form>

      <hr className="mk-auth-rule" />
      <div className="mk-auth-foot">
        <button type="button" className="mk-auth-alt" onClick={() => void signOut()}>
          {t('common.signOut')}
        </button>
      </div>
    </AuthPage>
  );
}
